import React, { useState, useEffect, createRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import Colours from '../definitions/Colours';
import Typography from '../definitions/Typography';
import MessagePropType from '../definitions/MessagePropType';
import debounce from '../functions/debounce';
import toKebabCase from '../functions/toKebabCase';
import messageToString from '../functions/messageToString';


function HorizontalTabs({
    activeTabIndex,
    initialTabIndex = 0,
    tabs,

    onTabChange,

    tabPanelIdPrefix,
    tabIdPrefix,

    isCondensed = false,

    className,
    ...otherProps
}) {
    const intl = useIntl();

    // This MUST be an index
    const [ activeTab, setActiveTab ] = useState(activeTabIndex ?? initialTabIndex);

    const [ activeTabElements ] = useState(Array(tabs.length).fill().map(() => createRef()));
    const [ underlinePositionLeft, setUnderlinePositionLeft ] = useState();
    const [ underlineWidth, setUnderlineWidth ] = useState();

    const isControlled = typeof activeTabIndex === 'number';

    // Update activeTab state when the controlled prop changes
    useEffect(() => {
        if (isControlled) {
            setActiveTab(activeTabIndex);
        }
    }, [ activeTabIndex ]);

    // Calculate position & size of underline
    const updateActiveTabUnderlineIndicator = debounce(() => {
        const activeTabRef = activeTabElements?.[activeTab]?.current;

        if (activeTabRef) {
            setUnderlinePositionLeft(activeTabRef.offsetLeft);
            setUnderlineWidth(activeTabRef.clientWidth);
        }
    }, 150);

    // Whenever the activeTab changes, recalculate position of bolder underline for active tab
    useEffect(() => {
        updateActiveTabUnderlineIndicator();

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateActiveTabUnderlineIndicator);
        }

        return () => window?.removeEventListener('resize', updateActiveTabUnderlineIndicator);
    }, [ activeTab ]);

    function handleTabClick(index) {
        if (!isControlled) {
            setActiveTab(index);
        }

        onTabChange && onTabChange(index);
    }

    const tabsAsStrings = tabs.map(tab => ({
        title: messageToString(tab.title, intl),
        subTitle: messageToString(tab.subTitle, intl),
    }));

    return (
        <Container
            className={classNames(
                className,
                'rh-display-flex rh-position-relative rh-align-items-stretch rh-m-0 rh-pl-0',
            )}
            role="tablist"
            linePositionLeft={underlinePositionLeft}
            lineWidth={underlineWidth}
            {...otherProps}
        >
            <For
                each="tab"
                of={tabsAsStrings}
                index="tabIndex"
            >
                <li
                    key={`child-${removeFormatting(tab.title)}-${tabIndex}`}
                    className={classNames(
                        'tab rh-display-flex rh-align-items-flex-start',
                        {
                            'active': tabIndex === activeTab,

                            'rh-mr-1': tabIndex === 0, // only left margin on the first tab
                            'rh-ml-1': tabIndex === tabsAsStrings.length - 1, // only right margin on last tab
                            'rh-mx-1': tabIndex !== 0 && tabIndex !== tabsAsStrings.length - 1, // left/right margin on all other tabs
                        }
                    )}
                    ref={activeTabElements[tabIndex]}
                    role="presentation"
                >
                    <button
                        type="button"
                        role="tab"
                        className={classNames(
                            'tab-button rh-display-flex rh-align-items-center',
                            'leading-s rh-text-decoration-none rh-border-width-0',
                            'hover--rh-fg-blueberry',
                            {
                                'active': tabIndex === activeTab,
                                'rh-px-0_25 rh-py-1_5': !isCondensed,
                                'rh-pb-0_75': isCondensed,
                            },
                        )}
                        onClick={() => handleTabClick(tabIndex)}

                        // Clean the ID a bit since it's based on a title, which might have
                        // ampersands or spaces in it or whatever.
                        // Data-name can safely include a wider variety of characters.
                        id={`${tabIdPrefix}_${getSafeHTMLName(toKebabCase(tab.title))}`}
                        data-name={`${tabIdPrefix}_${toKebabCase(tab.title)}`}

                        aria-controls={`${tabPanelIdPrefix}_${toKebabCase(tab.title)}`}
                        aria-selected={tabIndex === activeTab}

                        // Prevents link from opening in iframe if displayed in a widget, see loader.jsx
                        data-widget-do-not-adjust-click="true"
                    >
                        <div className="rh-display-flex rh-flex-direction-column rh-align-items-center">
                            <p className="title rh-m-0 rh-text-m">
                                {tab.title}
                            </p>
                            <p className="sub-title rh-m-0 rh-text-s">
                                {tab.subTitle}
                            </p>
                        </div>
                    </button>
                </li>
            </For>

            <div className="selected-line rh-position-absolute rh-bg-blueberry-dark rh-zindex-elements" />
        </Container>
    );
}

HorizontalTabs.propTypes = {
    activeTabIndex: PropTypes.number,
    initialTabIndex: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([ null ]),
    ]),
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            title: MessagePropType.isRequired,
            subTitle: MessagePropType,
        }),
    ),

    onTabChange: PropTypes.func,

    tabPanelIdPrefix: PropTypes.string.isRequired,
    tabIdPrefix: PropTypes.string.isRequired,

    isCondensed: PropTypes.bool,
    className: PropTypes.string,
};

const Container = styled.ul`
    list-style: none;
    overflow: hidden;

    // this provides a separator below the tab bar
    &::after {
        content: '';
        display: block;
        width: 100%;
        height: 1px;

        background-color: ${Colours.STONE};

        position: absolute;
        left: 0;
        bottom: 1px;
    }

    > .tab {
        > .tab-button {
            &.active {
                font-weight: ${Typography.WEIGHTS.MEDIUM};
                color: ${Colours.BLUEBERRY_DARK};

                &:hover,
                &:focus {
                    color: ${Colours.BLUEBERRY_DARKEST};
                }
            }

            &:hover,
            &:focus {
                color: ${Colours.BLUEBERRY_DARK};
            }

            &:active {
                color: ${Colours.BLUEBERRY_DARKEST};
            }
        }
    }

    > .selected-line {
        left: 0;
        bottom: 0;

        width: ${props => props.lineWidth}px;
        height: 3px;

        transform: translateX(${props => props.linePositionLeft}px);

        transition:
            width 0.35s ease,
            transform 0.35s cubic-bezier(0.25, 1, 0.5, 1)
    };
`;

function removeFormatting(label) {
    return label.replace(/\s+/g,'').toLocaleLowerCase();
}

// @TODO: This is a duplicate of this function from web-components.
// Possibly consider moving to base-ui?
function getSafeHTMLName(string) {
    return string.replace(/[^A-Za-z0-6_\-:.]/g, '');
}

export default HorizontalTabs;
